import React, { useState, useMemo } from 'react';
import { useQuery, useMutation, gql } from '@apollo/client';
import MapFilters from '../components/AdvisorMap/MapFilters';
import MapTable from '../components/AdvisorMap/MapTable';
import TaskDetailModal from '../components/AdvisorMap/TaskDetailModal';
import AddTaskModal from '../components/AdvisorMap/AddTaskModal';
import AssessmentPromptEmptyState from '../components/Succession/AssessmentPromptEmptyState';
import { shouldShowAssessmentPrompt } from '../utils/unifiedIntake';
// SprintPickerModal will be implemented later; keep placeholder for now
const SprintPickerModal = () => null;
const getOverdueItems = (items) => items.filter(i => i.dueAt && new Date(i.dueAt) < new Date() && i.status !== 'completed');
const getItemsDueInDays = (items, days) => {
  const now = new Date();
  const end = new Date(now.getTime() + days * 24 * 60 * 60 * 1000);
  return items.filter(i => i.dueAt && new Date(i.dueAt) >= now && new Date(i.dueAt) <= end);
};
const getActiveSprint = () => null;

const ACTION_PLAN_QUERY = gql`
  query ActionPlan($id: ID) {
    actionPlan(id: $id) {
      id
      items {
        id
        title
        description
        status
        priority
        dueAt
        assigneeUserId
        domain
        sprintIds
        sprintNames
        taskTemplate { id key title description cepaCategory deadlineInDays domain }
        documents {
          id
          name
          download_url
          created_at
        }
      }
    }
  }
`;

const GET_CURRENT_USER_MIN = gql`
  query GetCurrentUserMinForAdvisorMap {
    getCurrentUser { id triggering_events { event_type completed_at } }
  }
`;

const LIST_SPRINTS = gql`
  query ListSprintsForAdvisorMap($ownerId: ID, $status: [SprintStatusEnum!]) {
    sprints(owner_user_id: $ownerId, status: $status) { id name startOn endOn status }
  }
`;

const TASK_TEMPLATES_QUERY = gql`
  query TaskTemplates($domain: String, $cepa: String) {
    taskTemplates(domain: $domain, cepaCategory: $cepa) { id title domain cepaCategory deadlineInDays }
  }
`;

// Adjust to field/arg names as defined in server mutation
const UPDATE_ITEM = gql`
  mutation UpdateActionPlanItem(
    $id: ID!,
    $status: ActionPlanItemStatusEnum,
    $priority: ActionPlanItemPriorityEnum,
    $dueAt: ISO8601DateTime,
    $assigneeUserId: ID
  ) {
    update_action_plan_item(
      id: $id,
      status: $status,
      priority: $priority,
      dueAt: $dueAt,
      assigneeUserId: $assigneeUserId
    ) {
      id
      title
      status
      priority
      dueAt
      assigneeUserId
    }
  }
`;

const AdvisorMapBacklogContainer = ({ actionPlanId }) => {
  const { data, loading, error, refetch } = useQuery(ACTION_PLAN_QUERY, { variables: { id: actionPlanId } });
  const { data: cuData } = useQuery(GET_CURRENT_USER_MIN);
  const { data: sprintsData } = useQuery(LIST_SPRINTS, { variables: { ownerId: cuData?.getCurrentUser?.id || null, status: null }, skip: !cuData?.getCurrentUser?.id });
  const { data: templatesData } = useQuery(TASK_TEMPLATES_QUERY, { variables: { domain: undefined, cepa: undefined } });
  const [updateItem] = useMutation(UPDATE_ITEM);

  // Required state per spec
  const [selectedItems, setSelectedItems] = useState([]);
  const [filters, setFilters] = useState({
    search: '',
    domains: [],
    cepaCategories: [],
    statuses: ['OPEN', 'IN_PROGRESS', 'BLOCKED'].map(s => s.toLowerCase()),
    dueDateFilter: 'all',
    assigneeIds: [],
    inSprintOnly: false,
  });
  const [sortField, setSortField] = useState('dueAt');
  const [sortDirection, setSortDirection] = useState('asc');
  const [isSprintModalOpen, setIsSprintModalOpen] = useState(false);
  const [isAddTaskModalOpen, setIsAddTaskModalOpen] = useState(false);
  const [isTaskDetailModalOpen, setIsTaskDetailModalOpen] = useState(false);
  const [selectedTaskForDetail, setSelectedTaskForDetail] = useState(null);

  const items = data?.actionPlan?.items || [];
  const currentUser = cuData?.getCurrentUser || null;
  const showPrompt = shouldShowAssessmentPrompt(currentUser);
  const sprints = sprintsData?.sprints || [];

  const formatRange = (start, end) => {
    try {
      if (!end) return '';
      const s = start ? new Date(start) : null;
      const e = new Date(end);
      const fmt = (d) => d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
      return `${s ? fmt(s) : ''} ${s ? '–' : ''} ${fmt(e)}`.trim();
    } catch (_) {
      return '';
    }
  };

  const filteredAndSortedItems = useMemo(() => {
    let result = items.filter((item) => {
      if (filters.search) {
        const q = filters.search.toLowerCase();
        if (!item.title?.toLowerCase().includes(q)) return false;
      }
      if (filters.statuses?.length && !filters.statuses.includes(item.status)) return false;
      if ((filters.domains || []).length > 0) {
        const domain = (item.domain || item.taskTemplate?.domain || '').toLowerCase();
        if (!filters.domains.includes(domain)) return false;
      }
      if ((filters.cepaCategories || []).length > 0) {
        const vmi = (item.cepaCategory || item.taskTemplate?.cepaCategory || '').toLowerCase();
        if (!filters.cepaCategories.includes(vmi)) return false;
      }
      if (filters.inSprintOnly) {
        const inAnySprint = Array.isArray(item.sprintIds) ? item.sprintIds.length > 0 : Boolean(item.sprintId);
        if (!inAnySprint) return false;
      }
      if (filters.dueDateFilter && filters.dueDateFilter !== 'all') {
        const due = item.dueAt ? new Date(item.dueAt) : null;
        const today = new Date();
        if (!due) return false;
        if (filters.dueDateFilter === 'overdue' && !(due < today && item.status !== 'completed')) return false;
        if (filters.dueDateFilter === 'next_7_days') {
          const next7 = new Date(today.getTime() + 7 * 24 * 60 * 60 * 1000);
          if (!(due >= today && due <= next7)) return false;
        }
      }
      return true;
    });

    result.sort((a, b) => {
      let av = a[sortField];
      let bv = b[sortField];
      if (sortField === 'dueAt') {
        av = av ? new Date(av).getTime() : 0;
        bv = bv ? new Date(bv).getTime() : 0;
      } else if (sortField === 'sprint') {
        // Sort by first sprint name (or empty string when none)
        const aNames = Array.isArray(a.sprintNames) ? a.sprintNames.join(', ') : '';
        const bNames = Array.isArray(b.sprintNames) ? b.sprintNames.join(', ') : '';
        av = aNames.toLowerCase();
        bv = bNames.toLowerCase();
      } else if (sortField === 'cepaCategory') {
        // VMI Category may be on item or template; compare alphabetically
        const aVal = (a.cepaCategory || a.taskTemplate?.cepaCategory || '').toString();
        const bVal = (b.cepaCategory || b.taskTemplate?.cepaCategory || '').toString();
        av = aVal.toLowerCase();
        bv = bVal.toLowerCase();
      }
      if (typeof av === 'string' && typeof bv === 'string') {
        av = av.toLowerCase();
        bv = bv.toLowerCase();
      }
      if (av < bv) return sortDirection === 'asc' ? -1 : 1;
      if (av > bv) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });

    return result;
  }, [items, filters, sortField, sortDirection]);

  const itemCounts = useMemo(() => {
    // Status counts across ALL items (not filtered)
    const statusCounts = items.reduce((acc, it) => {
      const s = String(it.status || '').toLowerCase();
      acc[s] = (acc[s] || 0) + 1;
      return acc;
    }, { open: 0, in_progress: 0, blocked: 0, completed: 0, wont_do: 0 });

    const totalOpen = (statusCounts.open || 0) + (statusCounts.in_progress || 0) + (statusCounts.blocked || 0);
    const inSprint = items.filter((i) => Array.isArray(i.sprintIds) ? i.sprintIds.length > 0 : Boolean(i.sprintId)).length;
    const overdue = getOverdueItems(items).length;

    return {
      total: items.length,
      byDomain: { personal: 0, business: 0 },
      byCEPA: { identify: 0, protect: 0, build: 0, harvest: 0, manage: 0 },
      byStatus: statusCounts,
      totalOpen,
      inSprint,
      overdue,
      dueNext7Days: getItemsDueInDays(items, 7).length,
      dueThisMonth: getItemsDueInDays(items, 30).length,
    };
  }, [items]);

  const handleItemUpdate = async (id, updates) => {
    const toEnum = (val) => (val ? String(val).toUpperCase() : undefined);
    const vars = { id };
    if (updates.status) vars.status = toEnum(updates.status);
    if (updates.priority) vars.priority = toEnum(updates.priority);
    if (updates.dueAt) vars.dueAt = updates.dueAt;
    if (updates.assigneeUserId) vars.assigneeUserId = updates.assigneeUserId;
    await updateItem({ variables: vars });
    refetch();
    try { window.dispatchEvent(new CustomEvent('action-plan-updated')); } catch(_) {}
  };

  const handleTaskDetailOpen = (task) => {
    setSelectedTaskForDetail(task);
    setIsTaskDetailModalOpen(true);
  };

  const handleBulkAddToSprint = (ids) => {
    setSelectedItems(ids);
    setIsSprintModalOpen(true);
  };

  const handleBulkRemoveFromSprint = async (_ids) => {
    // server mutation would go here; refresh from server
    await refetch();
  };

  const handleBulkMarkCompleted = async (ids) => {
    for (const id of ids) {
      await handleItemUpdate(id, { status: 'completed' });
    }
    try { window.dispatchEvent(new CustomEvent('action-plan-updated')); } catch(_) {}
  };

  const handleFiltersChange = (next) => setFilters(next);
  const handleSortChange = (field, direction) => { setSortField(field); setSortDirection(direction); };

  const CREATE_ITEM = gql`
    mutation CreateActionPlanItem(
      $actionPlanId: ID!
      $title: String
      $description: String
      $domain: String
      $cepaCategory: String
      $priority: ActionPlanItemPriorityEnum
      $dueAt: ISO8601DateTime
    ) {
      create_action_plan_item(
        action_plan_id: $actionPlanId,
        title: $title,
        description: $description,
        domain: $domain,
        cepa_category: $cepaCategory,
        priority: $priority,
        due_at: $dueAt
      ) {
        id
        title
        status
        priority
        dueAt
        assigneeUserId
      }
    }
  `;
  const [createItem] = useMutation(CREATE_ITEM);

  const handleAddTaskSave = async (taskData) => {
    // taskData: { title, description, domain, cepaCategory, status, priority, dueAt }
    // We create a generic item against a selected template; for now, use a simple fallback template id via metadata mapping if available
    // In absence of template selection UI, create a minimal item by selecting the first template matching domain/cepa if needed.
    const planId = data?.actionPlan?.id;
    if (!planId) return;
    await createItem({
      variables: {
        actionPlanId: planId,
        title: taskData.title,
        description: taskData.description,
        domain: taskData.domain,
        cepaCategory: taskData.cepaCategory,
        priority: String(taskData.priority || 'MEDIUM').toUpperCase(),
        dueAt: taskData.dueAt ? new Date(taskData.dueAt).toISOString() : null,
      },
    });
    setIsAddTaskModalOpen(false);
    refetch();
  };

  if (loading) return <div>Loading...</div>;
  if (error) return <div>Error loading action plan</div>;

  const activeSprint = getActiveSprint();

  return (
    <div className="space-y-6">
      <div className="bg-succession-bg-primary border-b border-succession-border">
        <div className="px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="space-y-1">
              <h1 className="text-xl font-semibold text-succession-text-primary">Master Action Plan</h1>
              <p className="text-xs text-succession-text-secondary">Manage and track action items across all client engagements</p>
            </div>
            <div className="flex flex-col items-end gap-2">
              <select
                className="text-sm border border-succession-border bg-succession-bg-primary text-succession-text-primary rounded-md px-3 py-1"
                value=""
                onChange={(e) => {
                  const id = e.target.value;
                  if (!id) return;
                  if (id === '__MAP__') { window.location.href = '/succession/advisor-map'; return; }
                  window.location.href = `/succession/sprint?sprintId=${encodeURIComponent(id)}`;
                }}
              >
                <option value="" disabled>View Master Plan or Sprint...</option>
                <option value="__MAP__">Master Action Plan</option>
                {sprints.map((sp) => {
                  const range = formatRange(sp.startOn, sp.endOn);
                  const label = range ? `${sp.name} — ${range}` : sp.name;
                  return <option key={sp.id} value={sp.id}>{label}</option>;
                })}
              </select>
              <div className="flex items-center gap-3">
              <div className="hidden md:flex items-center gap-4 mr-2 text-xs text-succession-text-secondary">
                <div className="flex items-center gap-2">
                  <span className="inline-block w-2 h-2 rounded-full bg-blue-500" />
                  <span>Value Acceleration</span>
                </div>
              </div>
              <button className="flex items-center gap-2 px-3 py-2 border border-succession-border rounded text-sm text-succession-text-primary">Export</button>
              <button className="flex items-center gap-2 px-3 py-2 bg-succession-primary hover:bg-succession-primary/90 text-white rounded text-sm" onClick={() => setIsAddTaskModalOpen(true)}>Add Task</button>
              </div>
            </div>
          </div>

          {/* Metrics row */}
          <div className="mt-4 flex flex-wrap items-center gap-6 text-xs text-succession-text-secondary">
            <div className="flex items-center gap-2">
              <span className="inline-block w-2 h-2 rounded-full bg-blue-500" />
              <span className="font-medium">Total Open</span>
              <span className="ml-1 text-succession-text-muted">{itemCounts?.totalOpen || 0}</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="inline-block w-2 h-2 rounded-full bg-green-500" />
              <span className="font-medium">In Sprint</span>
              <span className="ml-1 text-succession-text-muted">{itemCounts?.inSprint || 0}</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="inline-block w-2 h-2 rounded-full bg-red-500" />
              <span className="font-medium">Overdue</span>
              <span className="ml-1 text-succession-text-muted">{itemCounts?.overdue || 0}</span>
            </div>
            {activeSprint && (
              <div className="flex items-center gap-2 ml-auto text-succession-text-secondary">
                <span className="inline-block w-2 h-2 rounded-full bg-emerald-500" />
                <span className="font-medium">Active Sprint:</span>
                <span className="text-succession-text-secondary">Q1 Value Acceleration</span>
              </div>
            )}
          </div>
        </div>
      </div>

      <MapFilters filters={filters} onFiltersChange={handleFiltersChange} itemCounts={itemCounts} />

      <div className="p-6">
        <div className="bg-succession-bg-primary border border-succession-border rounded-lg">
          <div className="px-6 py-3 border-b border-succession-border bg-succession-bg-secondary/50">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-succession-text-primary">Action Items ({filteredAndSortedItems.length})</h2>
              <div className="text-sm text-succession-text-muted">Sorted by {sortField} ({sortDirection})</div>
            </div>
          </div>
          {showPrompt ? (
            <div className="p-6">
              <AssessmentPromptEmptyState placement="advisor_map" />
            </div>
          ) : (
            <MapTable
              items={filteredAndSortedItems}
              selectedItems={selectedItems}
              onSelectionChange={setSelectedItems}
              onItemUpdate={handleItemUpdate}
              onItemClick={handleTaskDetailOpen}
              onBulkAddToSprint={handleBulkAddToSprint}
              onBulkRemoveFromSprint={handleBulkRemoveFromSprint}
              onBulkMarkCompleted={handleBulkMarkCompleted}
              sortField={sortField}
              sortDirection={sortDirection}
              onSortChange={handleSortChange}
            />
          )}
        </div>
      </div>

      <SprintPickerModal
        isOpen={isSprintModalOpen}
        onClose={() => setIsSprintModalOpen(false)}
        onSelectSprint={() => setIsSprintModalOpen(false)}
        onCreateSprint={() => setIsSprintModalOpen(false)}
        selectedItemCount={selectedItems.length}
      />

      <AddTaskModal
        isOpen={isAddTaskModalOpen}
        onClose={() => setIsAddTaskModalOpen(false)}
        onSave={handleAddTaskSave}
      />

      <TaskDetailModal
        isOpen={isTaskDetailModalOpen}
        onClose={() => setIsTaskDetailModalOpen(false)}
        task={selectedTaskForDetail}
        onSave={async (id, updates) => { await handleItemUpdate(id, updates); }}
        onMarkCompleted={async (id) => { await handleItemUpdate(id, { status: 'completed' }); }}
        onDelete={async (_id) => { setIsTaskDetailModalOpen(false); }}
        onTaskUpdated={async () => {
          const result = await refetch();
          // Update selectedTaskForDetail with fresh data after refetch
          if (selectedTaskForDetail && result.data?.actionPlan?.items) {
            const updatedTask = result.data.actionPlan.items.find((item) => item.id === selectedTaskForDetail.id);
            if (updatedTask) {
              setSelectedTaskForDetail(updatedTask);
            }
          }
        }}
      />
    </div>
  );
};

export default AdvisorMapBacklogContainer;


